import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractComponent, FilterItem } from '@components';
import { debounceTime, Subject } from 'rxjs';
import { environment } from '@environment';

export class FilterGroup {
  constructor(
      public key: string,
      public queryParam: string,
      public labelKey: string,
      public type: FilterGroupType = FilterGroupType.SINGLE_SELECT,
      public allowSearching: boolean = false,
      public items: FilterItem[] = [],
      public requiredAny: boolean = false,
      public showSelectionAsLabel: boolean = false
) {}
}

export enum FilterGroupType {
  SINGLE_SELECT = 1,
  MULTI_SELECT = 2,
  SINGLE_SELECT_BUTTONS = 3
}

@Component({
  selector: 'app-filter-panel',
  templateUrl: './filter-panel.component.html',
  styleUrls: ['./filter-panel.component.scss'],
  standalone: false
})
export class FilterPanelComponent extends AbstractComponent implements OnInit {

  @Input() groups: FilterGroup[] = [];
  @Input() handleQueryParams = false;
  @Input() showFilterChips = true;
  @Input() showSearchBar = true;
  @Input() searchTermParam: string = 'searchTerm';
  @Input() minTextSearchLength: number | undefined;
  @Output() filterChange = new EventEmitter<Record<string, any>>();

  searchText = '';

  private filterChange$ = new Subject<void>();

  constructor(private router: Router, private route: ActivatedRoute) {
    super();
  }

  ngOnInit(): void {
    if (this.handleQueryParams) {
      this.applyQueryParams();
    }
    this.subscriptions.push(
        this.filterChange$.pipe(debounceTime(environment.fullTextSearchDelay)).subscribe(() => {
          if (this.handleQueryParams) {
            this.pushQueryParams();
          }
          this.filterChange.emit(this.buildResult());
        })
    );
  }


  isSelected(queryParam: string, id: number): boolean {
    return this.groups
        .find(g => g.queryParam === queryParam)
        ?.items.some(i => i.id === id && i.selected) ?? false;
  }

  toggle(group: FilterGroup, values: FilterItem[]): void {
    const ids = new Set(values.map(v => v.id));
    group.items.forEach(i => i.selected = ids.has(i.id));
    group.items = [...group.items];
    this.filterChanged();
  }

  removeFilter(group: FilterGroup, item: FilterItem): void {
    item.selected = false;
    this.filterChanged();
  }

  clearAllFilters(): void {
    this.groups.forEach(g => g.items.forEach(i => i.selected = false));
    this.searchText = '';
    this.filterChanged();
  }

  handleInput(value: string): void {
    this.searchText = value.trim();
    if (!this.minTextSearchLength || (this.minTextSearchLength && (!this.searchText.length || this.searchText.length >= this.minTextSearchLength))) {
      this.filterChanged();
    }
  }

  private filterChanged(): void {
    this.filterChange$.next();
  }

  private selectedIds(group: FilterGroup): number[] {
    return group.items.filter(i => i.selected && i.id != null).map(i => i.id!);
  }

  private buildResult(): Record<string, any> {
    const result: Record<string, any> = this.showSearchBar ? { [this.searchTermParam]: this.searchText || undefined } : {};
    this.groups.forEach(g => {
      const values = this.selectedIds(g);
      result[g.queryParam] = g.type === FilterGroupType.MULTI_SELECT ? values : (values[0] ?? null);
    });
    return result;
  }


  private applyQueryParams(): void {
    const params = this.route.snapshot.queryParams;
    if (this.showSearchBar) {
      this.searchText = params[this.searchTermParam] ?? '';
    }
    this.groups.forEach(g => {
      const raw = params[g.queryParam];
      if (!raw) return;
      const ids = new Set(String(raw).split(','));
      g.items.forEach(i => i.selected = ids.has(String(i.id)));
    });
  }

  private pushQueryParams(): void {
    const result = this.buildResult();
    const q: Record<string, any> = {};
    if (this.showSearchBar && result[this.searchTermParam]) q[this.searchTermParam] = result[this.searchTermParam];
    this.groups.forEach(g => {
      const v = result[g.queryParam];
      if (Array.isArray(v) && v.length) q[g.queryParam] = v.join(',');
      else if (v != null) q[g.queryParam] = v;
    });
    this.router.navigate([], { relativeTo: this.route, queryParams: q, replaceUrl: true });
  }
}
